'use client';

import { useState } from 'react';

import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import Icon from '@/components/ui/AppIcon';

interface FormData {
  name: string;
  email: string;
  phone: string;
  company: string;
  message: string;
}

export default function ContactPage() {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitStatus('success');
      setFormData({ name: '', email: '', phone: '', company: '', message: '' });
      
      setTimeout(() => setSubmitStatus('idle'), 5000);
    }, 1500);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const contactInfo = [
    {
      id: 'contact_address',
      icon: 'MapPinIcon',
      title: 'Visit Us',
      content: '1630 Terminal St',
      subcontent: 'West Sacramento, CA 95691',
    },
    {
      id: 'contact_phone',
      icon: 'PhoneIcon',
      title: 'Call Us',
      content: '(916) 407-2938',
      subcontent: 'Mon-Fri 8am-6pm PST',
    },
    {
      id: 'contact_email',
      icon: 'EnvelopeIcon',
      title: 'Email Us',
      content: 'support@armadamsp.net',
      subcontent: 'We respond within 24 hours',
    },
  ];

  return (
    <>
      <Header />
      <main className="min-h-screen bg-black pt-20">
        {/* Page Header */}
        <section className="py-32 px-6">
          <div className="max-w-7xl mx-auto text-center space-y-8">
            <span className="label-micro text-primary">04 // GET IN TOUCH</span>
            <h1 className="text-6xl md:text-8xl font-bold tracking-tighter uppercase leading-none">
              Let's Build <br />
              <span className="text-primary">Something Great.</span>
            </h1>
            <p className="text-muted-foreground font-mono text-sm md:text-base max-w-3xl mx-auto leading-relaxed">
              Ready to transform your IT infrastructure? Schedule a free consultation with our team. 
              We're here to help your business grow securely.
            </p>
          </div>
        </section>

        {/* Contact Info Cards */}
        <section className="py-16 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8">
              {contactInfo.map((info, index) => (
                <div
                  key={info.id}
                  className="card-glass p-8 rounded-3xl space-y-4 hover:border-primary/50 transition-all animate-fade-in-up"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center">
                    <Icon name={info.icon} className="text-primary" size={24} />
                  </div>
                  <div className="space-y-2">
                    <p className="label-micro text-muted-foreground">{info.title}</p>
                    <p className="text-xl font-bold text-white">{info.content}</p>
                    <p className="text-sm text-muted-foreground">{info.subcontent}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Map and Form Section */}
        <section className="py-16 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Map */}
              <div className="card-glass rounded-3xl overflow-hidden h-[500px]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3118.2!2d-121.5308!3d38.5805!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x809ad13b3c7f3f3f%3A0x1234567890!2s1630%20Terminal%20St%2C%20West%20Sacramento%2C%20CA%2095691!5e0!3m2!1sen!2sus!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Armada MSP Location"
                />
              </div>

              {/* Contact Form */}
              <div className="card-glass p-8 md:p-12 rounded-3xl space-y-8">
                <div className="space-y-4">
                  <h2 className="text-4xl font-bold tracking-tight">
                    Send Us a <span className="text-primary">Message</span>
                  </h2>
                  <p className="text-muted-foreground text-sm">
                    Fill out the form below and we'll get back to you within 24 hours.
                  </p>
                </div>

                {submitStatus === 'success' && (
                  <div className="bg-secondary/10 border border-secondary/20 rounded-2xl p-4 flex items-center gap-3">
                    <Icon name="CheckCircleIcon" className="text-secondary" size={24} />
                    <p className="text-secondary text-sm font-medium">
                      Thank you! We'll be in touch soon.
                    </p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label htmlFor="name" className="label-micro text-muted-foreground">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-black/50 border border-border rounded-xl text-white placeholder:text-muted-foreground focus:border-primary focus:outline-none transition-colors"
                        placeholder="John Doe"
                      />
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="email" className="label-micro text-muted-foreground">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-black/50 border border-border rounded-xl text-white placeholder:text-muted-foreground focus:border-primary focus:outline-none transition-colors"
                        placeholder="john@company.com"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label htmlFor="phone" className="label-micro text-muted-foreground">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-black/50 border border-border rounded-xl text-white placeholder:text-muted-foreground focus:border-primary focus:outline-none transition-colors"
                        placeholder="(916) 555-0123"
                      />
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="company" className="label-micro text-muted-foreground">
                        Company Name
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-black/50 border border-border rounded-xl text-white placeholder:text-muted-foreground focus:border-primary focus:outline-none transition-colors"
                        placeholder="Your Company"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="message" className="label-micro text-muted-foreground">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="w-full px-4 py-3 bg-black/50 border border-border rounded-xl text-white placeholder:text-muted-foreground focus:border-primary focus:outline-none transition-colors resize-none"
                      placeholder="Tell us about your IT needs..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full px-8 py-4 bg-primary text-primary-foreground rounded-full label-micro hover:scale-105 transition-all btn-glow disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* Additional CTA */}
        <section className="py-32 px-6 bg-black border-t border-border">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <h2 className="text-5xl md:text-7xl font-bold tracking-tighter uppercase leading-none">
              Prefer to <br />
              <span className="text-primary">Call Directly?</span>
            </h2>
            <p className="text-muted-foreground font-mono text-sm max-w-2xl mx-auto">
              Our team is available Monday through Friday, 8am-6pm PST. 
              We're always happy to discuss your IT needs.
            </p>
            <a
              href="tel:+19164072938"
              className="inline-flex items-center gap-3 px-12 py-4 bg-secondary text-secondary-foreground rounded-full label-micro hover:scale-105 transition-all"
            >
              <Icon name="PhoneIcon" size={20} />
              (916) 407-2938
            </a>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}